import { group } from 'console'
import { GetterTree } from 'vuex'

import { QualificationsItem, StatusData, UnitsItem } from '../../typings/StatusData'
import { Dictionary, StateInterface } from './state.interface'

export const getters: GetterTree<StateInterface, any> = {
  getCurrentAlarmId: (state): number | null => {
    const alarmsList = Object.keys(state.alarm.items).map((key) => {
      return state.alarm.items[key].closed ? null : state.alarm.items[key]
    }).filter(elem => elem !== null)

    const currentAlarm = alarmsList.sort((a: any, b: any) => {
      const tsCreateA = a.ts_create
      const tsCreateB = b.ts_create
      if (tsCreateA < tsCreateB) return 1
      if (tsCreateA > tsCreateB) return -1
      return 0
    })

    return currentAlarm[0]?.id || null
  },

  getQualificationName: (state): Function => (id: number): string => {
    if (!(Object.keys(state.cluster.qualification)).includes(id.toString())) return id.toString()
    return state.cluster.qualification[id].name
  },

  getQualificationShortname: (state): Function => (id: number): string => {
    if (!(Object.keys(state.cluster.qualification)).includes(id.toString())) return id.toString()
    return state.cluster.qualification[id].shortname
  },

  getCurrentClusterId: (state): number => {
    return state.ucr[state.ucr_active].cluster_id
  },

  getMonitorUnitsData: (state): Function => (monitorId: number, statusId: number, sortBy: number, groupId: number | 'all' | 'none'): Array<any> => {
    let units: UnitsItem[] = state.monitor[monitorId][statusId].all

    units = units.map((unit: UnitsItem) => {
      // As js passes the `unit` object as a reference,
      // we dont need to push it to a new object and can mutate
      // it directly within the passed `units` array
      unit.shortname = state.cluster.consumer[unit.id.toString()].stdformat_name
      unit.firstname = state.cluster.consumer[unit.id.toString()].firstname
      unit.lastname = state.cluster.consumer[unit.id.toString()].lastname
      unit.qualifications = state.cluster.consumer[unit.id.toString()].qualifications
      unit.groups = state.cluster.consumer[unit.id.toString()].groups

      return unit
    })

    // Filter units by groupId relation
    if (groupId !== 'all' && groupId !== 'none') {
      // @ts-ignore
      units = units.filter((elem) => elem.groups.includes(parseInt(groupId, 10)))
    }

    // Filter units by no group relation
    if (groupId !== 'all' && groupId === 'none') {
      units = units.filter((elem) => elem.groups.length === 0)
    }

    switch (sortBy) {
    case 1:
      // Sorting based on firstname (sortBy = 1)
      units = units.sort((a: any, b: any) => {
        const firstnameA = a.firstname.toUpperCase() // Ignore cases
        const firstnameB = b.firstname.toUpperCase() // Ignore cases
        if (firstnameA < firstnameB) return -1
        if (firstnameA > firstnameB) return 1
        return 0
      })
      break
    case 2:
      // Sorting based on lastname (sortBy = 2)
      units = units.sort((a: any, b: any) => {
        const lastnameA = a.lastname.toUpperCase() // Ignore cases
        const lastnameB = b.lastname.toUpperCase() // Ignore cases
        if (lastnameA < lastnameB) return -1
        if (lastnameA > lastnameB) return 1
        return 0
      })
      break
    default:
      // Default sorting falls back to timedate-based sorting (sortBy = 3)
      units = units.sort((a: any, b: any) => a.ts - b.ts)
    }

    return units
  },

  getMonitorStatusQualifications: (_state, getters): Function => (qualificationIdList: Dictionary<number>, sortingList: Array<string | number> = []): Array<any> => {
    let qualifications = new Array()

    if (sortingList.length === 0) return qualifications

    // Shorten the list and exclude missing elements based on qualificationsorting
    for (const key in qualificationIdList) {
      if (sortingList.length > 0 && !sortingList.includes(key.toString())) continue

      const qualificationItem: QualificationsItem = {
        id: parseInt(key, 10),
        shortname: getters.getQualificationShortname(key),
        count: qualificationIdList[key]
      }

      qualifications.push(qualificationItem)
    }

    // Sort the qualifications based on qualificationsorting
    // if more than one element exsits
    if (qualifications.length > 1) {
      qualifications = qualifications.sort(function (a, b) {
        return sortingList.indexOf(a.id.toString()) - sortingList.indexOf(b.id.toString())
      })
    }

    return qualifications
  },

  getMonitorStatusData: (state, getters): Function => (
    sortingListStatus: Array<string | number> = [],
    sortingListQualifications: Array<string | number> = [],
    isDetailedData: boolean = false,
    sortDetailedDataBy: number = 0,
    selectedGroup: string = 'all'
  ): Array<any> => {
    const monitorId = isDetailedData ? 2 : 1
    const monitor = state.monitor[monitorId]
    let statusDataList = [] as any[]
    let sortedMonitor = [] as any[]

    // Shorten the list and exclude missing elements based on statussorting
    for (const key in monitor) {
      if (sortingListStatus.length > 0 && !sortingListStatus.includes(key.toString())) continue
      sortedMonitor.push({ id: parseInt(key, 10), ...monitor[key] })
    }

    // Sort the status based on statussorting
    // if more than one element exsits
    if (sortedMonitor.length > 1) {
      sortedMonitor = sortedMonitor.sort(function (a, b) {
        return sortingListStatus.indexOf(a.id.toString()) - sortingListStatus.indexOf(b.id.toString())
      })
    }

    // Populate with data
    for (let elem of sortedMonitor) {
      const statusSetting = state.cluster.status[elem.id]

      const unitsData = isDetailedData ? getters.getMonitorUnitsData(monitorId, elem.id, sortDetailedDataBy, selectedGroup) : null

      const statusData: StatusData = {
        id: parseInt(elem.id, 10),
        name: statusSetting['name'],
        color: statusSetting['color_hex'],
        sum: isDetailedData ? null : monitor[elem.id].all,
        units: unitsData,
        qualifications: getters.getMonitorStatusQualifications(monitor[elem.id].qualification, sortingListQualifications)
      }

      statusDataList.push(statusData)
    }

    return statusDataList
  }
}

export default getters
